/**
 * Grouping the questions a class is asking, so a teacher answers a topic once
 * instead of five students separately.
 *
 * This is deliberately a pure function over plain data, with no network and no
 * React: it is the part that has to be right, so it has to be testable on its
 * own. When the API gains a server-side grouping, it returns clusters in this
 * same shape and `useQuestionClusters` prefers them — nothing else changes.
 *
 * The local pass is a similarity join, not a model: normalise, drop the words
 * every question contains, then group anything that shares enough meaningful
 * ground. It is conservative on purpose. A near-miss that leaves two clusters
 * costs a teacher one extra answer; a wrong merge tells four students their
 * question was something else.
 */

/** Words that carry no topic. Kept small - over-stripping merges real topics. */
const STOPWORDS = new Set([
  "a", "an", "the", "is", "are", "was", "were", "be", "been", "being", "am",
  "do", "does", "did", "doing", "have", "has", "had", "having",
  "i", "we", "you", "he", "she", "it", "they", "me", "us", "him", "her", "them",
  "my", "our", "your", "his", "its", "their", "this", "that", "these", "those",
  "and", "or", "but", "if", "so", "than", "then", "as", "of", "to", "in", "on",
  "at", "for", "with", "from", "by", "about", "into", "over", "up", "down",
  "can", "could", "will", "would", "should", "shall", "may", "might", "must",
  "what", "why", "how", "when", "where", "which", "who", "whom",
  "not", "no", "yes", "please", "sir", "miss", "again", "also", "just", "very",
  "understand", "explain", "question", "sorry", "sure", "know", "get", "got",
  "some", "any", "all", "one", "more", "much", "many", "still", "again",
]);

/** Crude but predictable stemming: plurals and common verb endings only. */
export const stem = (word) => {
  let w = word;
  if (w.length > 4 && w.endsWith("ies")) return `${w.slice(0, -3)}y`;
  if (w.length > 4 && w.endsWith("ses")) return w.slice(0, -2);
  if (w.length > 3 && w.endsWith("s") && !w.endsWith("ss")) w = w.slice(0, -1);
  if (w.length > 5 && w.endsWith("ing")) w = w.slice(0, -3);
  if (w.length > 4 && w.endsWith("ed")) w = w.slice(0, -2);
  return w;
};

export const tokenise = (text) =>
  (text || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .filter((w) => !STOPWORDS.has(w))
    .map(stem)
    .filter((w) => w.length > 2);

/** Overlap over the smaller set, so a short question can still match a long
 *  one that contains it — "what is osmosis" against a rambling version. */
export const similarity = (a, b) => {
  if (!a.size || !b.size) return 0;
  let shared = 0;
  a.forEach((t) => {
    if (b.has(t)) shared += 1;
  });
  return shared / Math.min(a.size, b.size);
};

const THRESHOLD = 0.5;

const sharedCount = (a, b) => {
  let n = 0;
  a.forEach((t) => {
    if (b.has(t)) n += 1;
  });
  return n;
};

/**
 * Two shared words make a topic rather than a coincidence — but a question
 * can legitimately carry only one meaningful word ("what is osmosis"), and
 * that is the most common thing a class asks. So the bar is two, or the whole
 * of the shorter question when it is shorter than that.
 */
const minShared = (a, b) => Math.min(2, Math.min(a.size, b.size));

/**
 * @param {Array<{id, text, studentName, studentGuid, createdAt}>} questions
 * @returns {Array<cluster>} newest-first by the time the cluster last grew
 */
export function clusterQuestions(questions = [], { threshold = THRESHOLD } = {}) {
  const prepared = questions
    .filter((q) => q && q.text && q.text.trim())
    .map((q) => ({ ...q, tokens: new Set(tokenise(q.text)) }));

  const clusters = [];

  prepared.forEach((q) => {
    // Join the cluster it matches best, not merely the first one it matches.
    let best = null;
    let bestScore = 0;
    clusters.forEach((cluster) => {
      const score = similarity(q.tokens, cluster.tokens);
      if (
        score >= threshold &&
        sharedCount(q.tokens, cluster.tokens) >= minShared(q.tokens, cluster.tokens) &&
        score > bestScore
      ) {
        best = cluster;
        bestScore = score;
      }
    });

    if (best) {
      best.questions.push(q);
      // The cluster's vocabulary is what its members have in common, so it
      // cannot drift into a different topic as it grows.
      best.tokens = new Set([...best.tokens].filter((t) => q.tokens.has(t)));
      return;
    }

    clusters.push({ tokens: new Set(q.tokens), questions: [q] });
  });

  return clusters
    .map((cluster) => {
      const times = cluster.questions.map((q) => new Date(q.createdAt).getTime()).filter((t) => !Number.isNaN(t));
      const askedBy = [...new Map(cluster.questions.map((q) => [q.studentGuid || q.studentName, q])).values()];
      return {
        id: cluster.questions.map((q) => q.id).join("+"),
        questions: cluster.questions,
        askedBy,
        count: askedBy.length,
        // The shortest question is the clearest statement of the topic; a
        // generated summary would be a claim about what students meant.
        headline: [...cluster.questions].sort((a, b) => a.text.length - b.text.length)[0].text,
        keywords: [...cluster.tokens].slice(0, 4),
        firstAskedAt: times.length ? Math.min(...times) : null,
        lastAskedAt: times.length ? Math.max(...times) : null,
      };
    })
    .sort((a, b) => (b.lastAskedAt || 0) - (a.lastAskedAt || 0));
}

/**
 * How much this cluster needs answering: how many asked, and how long they
 * have waited. Used to order the rail and to decide what is urgent.
 */
export const clusterPriority = (cluster, now = Date.now()) => {
  const waitedMinutes = cluster.firstAskedAt ? (now - cluster.firstAskedAt) / 60000 : 0;
  return cluster.count * 2 + Math.min(waitedMinutes, 15);
};

export const waitedMinutes = (cluster, now = Date.now()) =>
  cluster.firstAskedAt ? Math.max(0, Math.round((now - cluster.firstAskedAt) / 60000)) : 0;
